import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  AlertTitle,
  Chip,
} from "@mui/material";
import {
  Warning as WarningIcon,
  Block as BlockIcon,
  GppBad as GppBadIcon,
} from "@mui/icons-material";
import OutfitUploadForm from "../components/ImageUpload/OutfitUploadForm";
import { OutfitMetadata } from "../types";
import UploadServiceInstance from "../services/upload-service";
import enhancedAuthService from "../services/enhanced-auth-service";
import { ContentModerationError, UploadBlockedError } from "../services/upload-service";
import UploadPipelineProgress from "../components/Upload/UploadPipelineProgress";
import { useUploadPipeline } from "../hooks/useUploadPipeline";

// Steps in the upload process
const steps = ["Upload Image", "Add Details", "Processing", "Complete"];

// Session violation tracking key
const SESSION_VIOLATIONS_KEY = 'upload_session_violations';

interface SessionViolationData {
  count: number;
  lastViolationAt: string;
  sessionStart: string;
}

/**
 * Get session violation data from sessionStorage
 */
function getSessionViolations(): SessionViolationData {
  try {
    const stored = sessionStorage.getItem(SESSION_VIOLATIONS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return {
    count: 0,
    lastViolationAt: '',
    sessionStart: new Date().toISOString(),
  };
}

/**
 * Increment session violation count
 */
function recordSessionViolation(): SessionViolationData {
  const data = getSessionViolations();
  data.count += 1;
  data.lastViolationAt = new Date().toISOString();
  sessionStorage.setItem(SESSION_VIOLATIONS_KEY, JSON.stringify(data));
  return data;
}

/**
 * Upload page component with content moderation warnings and blocking
 */
const UploadPage: React.FC = () => {
  const navigate = useNavigate();

  // Upload flow state
  const [activeStep, setActiveStep] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedOutfitId, setUploadedOutfitId] = useState<string | null>(null);

  // Pipeline progress tracking
  const pipeline = useUploadPipeline();

  // Moderation state
  const [moderationWarning, setModerationWarning] = useState<string | null>(null);
  const [moderationSeverity, setModerationSeverity] = useState<'warning' | 'error'>('warning');
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockInfo, setBlockInfo] = useState<{
    message: string;
    isPermanent: boolean;
    expiresAt?: string;
    remainingHours?: number;
    violationCount: number;
  } | null>(null);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [warningDialogContent, setWarningDialogContent] = useState<{
    title: string;
    message: string;
    severity: 'warning' | 'error';
    violationCount: number;
  } | null>(null);

  // Session-level tracking
  const [sessionViolations, setSessionViolations] = useState<number>(0);

  // Load session violations on mount
  useEffect(() => {
    const data = getSessionViolations();
    setSessionViolations(data.count);

    // If user has 3+ session violations, show a persistent warning
    if (data.count >= 3) {
      setModerationWarning(
        `You have had ${data.count} content violations in this session. Further violations will result in your account being suspended. Please only upload appropriate fashion content.`
      );
      setModerationSeverity('error');
    }
  }, []);

  // Warm upload lambdas
  const warmUploadLambdas = useCallback(async () => {
    try {
      const token = await enhancedAuthService.getAccessToken();
      await fetch(`https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev/warm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
    } catch (error) {
      console.warn('Lambda warming failed:', error);
    }
  }, []);

  /**
   * Handle moderation errors from the upload flow
   */
  const handleModerationError = useCallback((error: ContentModerationError) => {
    // Record session violation
    const sessionData = recordSessionViolation();
    setSessionViolations(sessionData.count);

    console.warn('[UploadPage] Content moderation violation', {
      action: error.moderationAction,
      violationCount: error.violationCount,
      sessionViolations: sessionData.count,
      blocked: error.blocked,
    });

    if (error.blocked) {
      // User has been blocked
      setIsBlocked(true);
      setBlockInfo({
        message: error.warningMessage || error.message,
        isPermanent: error.isPermanent,
        expiresAt: error.blockExpiresAt,
        remainingHours: error.remainingHours,
        violationCount: error.violationCount,
      });
      setShowBlockDialog(true);
    } else {
      // Show warning dialog
      const isFinalWarning = error.moderationAction === 'FINAL_WARN' || sessionData.count >= 2;
      setWarningDialogContent({
        title: isFinalWarning ? '⚠️ Final Warning' : '⚠️ Content Policy Violation',
        message: error.warningMessage || error.message,
        severity: isFinalWarning ? 'error' : 'warning',
        violationCount: error.violationCount || sessionData.count,
      });
      setShowWarningDialog(true);

      // Update persistent warning banner
      if (sessionData.count >= 2) {
        setModerationWarning(
          `You have ${sessionData.count} content violations. One more violation will result in your upload privileges being suspended.`
        );
        setModerationSeverity('error');
      } else {
        setModerationWarning(
          'Your last upload was rejected. Please ensure your photos are fashion-related and appropriate for all audiences.'
        );
        setModerationSeverity('warning');
      }
    }
  }, []);

  /**
   * Handle upload blocked errors
   */
  const handleBlockedError = useCallback((error: UploadBlockedError) => {
    setIsBlocked(true);
    setBlockInfo({
      message: error.message,
      isPermanent: error.isPermanent,
      expiresAt: error.expiresAt,
      remainingHours: error.remainingHours,
      violationCount: error.violationCount,
    });
    setShowBlockDialog(true);
  }, []);


  // Handle outfit upload
  const handleOutfitUpload = async (file: File, metadata: OutfitMetadata) => {
    // If user is blocked, don't allow upload
    if (isBlocked) {
      setShowBlockDialog(true);
      return;
    }

    try {
      setIsUploading(true);
      pipeline.reset();

      // Warm lambdas immediately
      warmUploadLambdas();

      // Move to processing step
      setActiveStep(2);

      // Progress callback from upload service
      const onProgress = (stepId: string) => {
        if (stepId === 'compress') {
          pipeline.setStepStatus('compress', 'active');
        } else if (stepId === 'upload') {
          pipeline.setStepStatus('compress', 'done');
          pipeline.setStepStatus('upload', 'active');
        } else if (stepId === 'backend_start') {
          pipeline.setStepStatus('upload', 'done');
          // Start animating backend steps (moderation → resize → AI → save → index)
          pipeline.startBackendAnimation();
        }
      };

      // Call the API to upload the outfit with a timeout
      const uploadPromise = UploadServiceInstance.uploadOutfit(file, metadata, onProgress);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Upload timed out. Please try again with a smaller image.')), 180000)
      );

      const uploadedOutfit = await Promise.race([uploadPromise, timeoutPromise]) as any;
      
      // All steps done
      pipeline.resolveAll();
      
      console.log('Upload completed, outfit:', uploadedOutfit);
      const outfitId = uploadedOutfit?.data?.id || uploadedOutfit?.id;
      console.log('Setting outfit ID:', outfitId);
      setUploadedOutfitId(outfitId);

      // Clear moderation warning on successful upload
      setModerationWarning(null);

      // Move to complete step
      setActiveStep(3);
    } catch (error: any) {
      console.error("Upload error:", error);

      // Mark pipeline failure
      if (error instanceof ContentModerationError) {
        pipeline.failAtCurrent('moderation');
      } else {
        pipeline.failAtCurrent();
      }

      // Go back to step 1 so user can retry
      setActiveStep(1);

      if (error instanceof ContentModerationError) {
        handleModerationError(error);
      } else if (error instanceof UploadBlockedError) {
        handleBlockedError(error);
      } else {
        // Generic error — show as alert
        setModerationWarning(error.message || 'Upload failed. Please try again.');
        setModerationSeverity('error');
      }
    } finally {
      setIsUploading(false);
    }
  };

  // Handle view outfit button click
  const handleViewOutfit = async () => {
    if (uploadedOutfitId) {
      await new Promise(resolve => setTimeout(resolve, 500));
      navigate(`/outfit/${uploadedOutfitId}`);
    }
  };

  // Handle upload another button click
  const handleUploadAnother = () => {
    setActiveStep(0);
    setUploadedOutfitId(null);
    pipeline.reset();
  };

  /**
   * Format remaining block time for display
   */
  const formatBlockTime = (hours?: number, expiresAt?: string): string => {
    if (!hours && !expiresAt) return '';
    if (hours && hours > 24) {
      const days = Math.ceil(hours / 24);
      return `${days} day${days > 1 ? 's' : ''}`;
    }
    if (hours) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    if (expiresAt) {
      const expires = new Date(expiresAt);
      return expires.toLocaleString();
    }
    return '';
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Upload Outfit
      </Typography>

      {/* Persistent moderation warning banner */}
      {moderationWarning && !isBlocked && (
        <Alert
          severity={moderationSeverity}
          sx={{ mb: 3 }}
          onClose={() => setModerationWarning(null)}
          icon={moderationSeverity === 'error' ? <GppBadIcon /> : <WarningIcon />}
        >
          <AlertTitle>
            {moderationSeverity === 'error' ? 'Content Policy Warning' : 'Upload Rejected'}
          </AlertTitle>
          {moderationWarning}
        </Alert>
      )}

      {/* Blocked state — replaces the entire upload flow */}
      {isBlocked && blockInfo && (
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', mb: 3 }}>
          <BlockIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Upload Privileges Suspended
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {blockInfo.message}
          </Typography>
          {!blockInfo.isPermanent && blockInfo.expiresAt && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label={`Suspended for ${formatBlockTime(blockInfo.remainingHours, blockInfo.expiresAt)}`}
                color="error"
                variant="outlined"
                sx={{ mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                Your upload privileges will be restored on {new Date(blockInfo.expiresAt).toLocaleString()}.
              </Typography>
            </Box>
          )}
          {blockInfo.isPermanent && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              This suspension is permanent due to repeated or severe content policy violations.
            </Typography>
          )}
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </Box>
        </Paper>
      )}

      {/* Normal upload flow — only shown when not blocked */}
      {!isBlocked && (
        <>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 && (
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                Upload Your Outfit Image
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Upload a high-quality image of your outfit. The image should clearly
                show all clothing items.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ maxWidth: 600, mx: 'auto' }}>
                Please ensure your photos are fashion-related and appropriate for all audiences.
                Images containing nudity, violence, minors, or non-fashion content will be rejected.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  warmUploadLambdas();
                  setActiveStep(1);
                }}
              >
                Continue
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <OutfitUploadForm
              onSubmit={handleOutfitUpload}
              isUploading={isUploading}
            />
          )}

          {activeStep === 2 && (
            <Paper elevation={2} className="marble-card" sx={{ p: 4, textAlign: "center", background: 'transparent' }}>
              <Typography variant="h5" gutterBottom>
                Processing Your Outfit
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                We&apos;re analyzing your outfit and preparing it for the community.
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                <UploadPipelineProgress steps={pipeline.steps} />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #1976d2',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
              </Box>
            </Paper>
          )}

          {activeStep === 3 && (
            <Paper elevation={2} className="marble-card" sx={{ p: 4, textAlign: "center", background: 'transparent' }}>
              <Typography variant="h5" gutterBottom>
                Upload Complete!
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Your outfit has been successfully uploaded and is now visible to the
                community.
              </Typography>
              <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 2 }}>
                <Button variant="contained" color="primary" onClick={handleViewOutfit}>
                  View Outfit
                </Button>
                <Button variant="outlined" color="primary" onClick={handleUploadAnother}>
                  Upload Another
                </Button>
              </Box>
            </Paper>
          )}
        </>
      )}


      {/* Warning Dialog — shown on content violations */}
      <Dialog
        open={showWarningDialog}
        onClose={() => setShowWarningDialog(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="moderation-warning-dialog-title"
      >
        <DialogTitle
          id="moderation-warning-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: warningDialogContent?.severity === 'error' ? 'error.main' : 'warning.main',
          }}
        >
          {warningDialogContent?.severity === 'error' ? <GppBadIcon /> : <WarningIcon />}
          {warningDialogContent?.title || 'Content Policy Violation'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {warningDialogContent?.message}
          </DialogContentText>
          {warningDialogContent && warningDialogContent.violationCount > 0 && (
            <Alert severity={warningDialogContent.severity} sx={{ mt: 2 }}>
              <AlertTitle>Violation Count: {warningDialogContent.violationCount}</AlertTitle>
              {warningDialogContent.violationCount >= 2
                ? 'You are close to having your upload privileges suspended. Please review our content guidelines carefully.'
                : 'Repeated violations will result in your upload privileges being suspended.'}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Content Guidelines:
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2 }}>
              <li>Only upload fashion-related photos (outfits, clothing items, accessories)</li>
              <li>No nudity, sexually explicit, or suggestive content</li>
              <li>No violent, graphic, or disturbing images</li>
              <li>No images of children or minors</li>
              <li>No drug, alcohol, or tobacco-focused content</li>
              <li>No hate symbols or offensive imagery</li>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowWarningDialog(false)}
            variant="contained"
            color="primary"
          >
            I Understand
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block Dialog — shown when user gets blocked */}
      <Dialog
        open={showBlockDialog}
        onClose={() => setShowBlockDialog(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="moderation-block-dialog-title"
      >
        <DialogTitle
          id="moderation-block-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <BlockIcon />
          Upload Privileges Suspended
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {blockInfo?.message || 'Your upload privileges have been suspended due to content policy violations.'}
          </DialogContentText>
          {blockInfo && !blockInfo.isPermanent && blockInfo.expiresAt && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>Temporary Suspension</AlertTitle>
              Your upload privileges will be restored on{' '}
              {new Date(blockInfo.expiresAt).toLocaleString()}{' '}
              ({formatBlockTime(blockInfo.remainingHours)} remaining).
            </Alert>
          )}
          {blockInfo?.isPermanent && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Permanent Suspension</AlertTitle>
              Your upload privileges have been permanently revoked due to repeated or severe
              content policy violations. This decision is final.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowBlockDialog(false);
              navigate('/');
            }}
            variant="contained"
            color="primary"
          >
            Return to Home
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadPage;
